import http from "@/services/http";
import {
  GarageListParams,
  GarageListItemDto,
  GarageResponseDto,
  CreateGarageDto,
  UpdateGarageDto,
} from "@/models/garage.model";

export interface GarageListResponse {
  items: GarageListItemDto[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

class GarageService {
  async listGarages(params: GarageListParams = {}): Promise<GarageListResponse> {
    const query = new URLSearchParams();
    const searchTerm = params.search?.trim();
    if (searchTerm) {
      query.set("search", searchTerm);
    }
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    const qs = query.toString();

    const response = await http.get<any>(`/garages${qs ? `?${qs}` : ""}`);

    // Manejo polimórfico: puede ser array directo o paginado
    if (Array.isArray(response)) {
      return {
        items: response,
        total: response.length,
        page: 1,
        pageSize: response.length,
        totalPages: 1,
      };
    }

    // Si es {success, data} sin paginación
    if (response?.success && Array.isArray(response?.data)) {
      return {
        items: response.data,
        total: response.data.length,
        page: 1,
        pageSize: response.data.length,
        totalPages: 1,
      };
    }

    // Paginado
    const items = response.items || response.data || [];
    const total =
      typeof response.totalCount === "number"
        ? response.totalCount
        : typeof response.total === "number"
          ? response.total
          : items.length;
    const page =
      typeof response.page === "number"
        ? response.page
        : typeof response.pageNumber === "number"
          ? response.pageNumber
          : 1;
    const pageSize =
      typeof response.pageSize === "number"
        ? response.pageSize
        : items.length || 10;

    return {
      items,
      total,
      page,
      pageSize,
      totalPages: response.totalPages || 1,
    };
  }

  async getGarageById(garageId: string): Promise<GarageResponseDto> {
    const response = await http.get<any>(`/garages/${garageId}`);
    // Si viene envuelto en {success, data}
    if (response?.success && response?.data) {
      return response.data;
    }
    return response;
  }

  async createGarage(data: CreateGarageDto): Promise<GarageResponseDto> {
    const response = await http.post<any>("/garages", data);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response;
  }

  async updateGarage(
    garageId: string,
    data: UpdateGarageDto
  ): Promise<GarageResponseDto> {
    const response = await http.put<any>(`/garages/${garageId}`, data);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response;
  }

  async deleteGarage(garageId: string): Promise<void> {
    await http.delete(`/garages/${garageId}`);
  }
}

export default new GarageService();
